/**
 * Рукопожатие ленты с расширением: «ты здесь?»
 *
 * Остальные сообщения мостика односторонние: лента кладёт обещание в окно и
 * не ждёт ответа. Для первого захода ответ обязателен — иначе «расширение
 * стоит» нельзя отличить от «скрипт на странице молчит», и человек узнает
 * об этом уже на живой вилке, когда полоса не появится.
 *
 * Молчание — это ответ. Расширения может не быть вовсе, и тогда ждать нечего.
 */

export const PING = 'forq:ping';
export const PONG = 'forq:pong';

/** Дольше человек на первом заходе не смотрит на крутящуюся строку. */
export const WAIT_MS = 800;

export function pingEnvelope(id: string): { channel: string; id: string } {
  return { channel: PING, id };
}

export function pongEnvelope(
  id: string,
  version: string,
): { channel: string; id: string; version: string } {
  return { channel: PONG, id, version };
}

function idOf(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null;
  const id = (data as { id?: unknown }).id;
  return typeof id === 'string' && id.length > 0 && id.length <= 64 ? id : null;
}

/** Разобрать вопрос. `null` — чужое или кривое. */
export function parsePing(data: unknown): { id: string } | null {
  if (typeof data !== 'object' || data === null) return null;
  if ((data as { channel?: unknown }).channel !== PING) return null;
  const id = idOf(data);
  return id === null ? null : { id };
}

/**
 * Разобрать ответ. Версия — из манифеста, не произвольный текст: чужое окно
 * не должно уметь представиться расширением одной строкой.
 */
export function parsePong(data: unknown): { id: string; version: string } | null {
  if (typeof data !== 'object' || data === null) return null;
  const body = data as { channel?: unknown; version?: unknown };
  if (body.channel !== PONG) return null;
  const id = idOf(data);
  const version = body.version;
  if (id === null || typeof version !== 'string' || !/^[0-9A-Za-z.+-]{1,32}$/.test(version)) {
    return null;
  }
  return { id, version };
}

let counter = 0;
const nextId = (): string => `${Date.now().toString(36)}-${(counter += 1)}`;

/**
 * Спросить расширение и дождаться версии. `null` — молчит.
 *
 * Окно параметром: тесты гоняют разговор без браузера, а вкладка ленты может
 * быть открыта внутри чужого окна.
 */
export async function pingExt(
  where: Window = window,
  wait = WAIT_MS,
): Promise<{ version: string } | null> {
  if (typeof where.postMessage !== 'function') return null;
  const id = nextId();
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      where.removeEventListener('message', hear);
      resolve(null);
    }, wait);
    function hear(event: MessageEvent): void {
      if (event.source !== where) return;
      const pong = parsePong(event.data);
      if (pong === null || pong.id !== id) return;
      clearTimeout(timer);
      where.removeEventListener('message', hear);
      resolve({ version: pong.version });
    }
    where.addEventListener('message', hear);
    try {
      where.postMessage(pingEnvelope(id), '*');
    } catch {
      clearTimeout(timer);
      where.removeEventListener('message', hear);
      resolve(null);
    }
  });
}
